/**
 * Invites HTTP service (PX-W6.2 manual-pass gap) — the guest side of the
 * invite flow: validate the token, then accept it. Both endpoints sit under
 * `/auth/invite/*` OUTSIDE the org-scoped tree (an invitee has no org yet),
 * so no `{orgId}` placeholder — the only W6.2 service with that property.
 *
 * Wire contracts (server AuthController):
 *  - `POST /auth/invite/validate` `{token}` → the invite row (email, orgName,
 *    role, inviterName, isExistingUser; the token itself never serialises).
 *  - `POST /auth/invite/accept` `{token, name?, password?}` → the LOGIN shape
 *    `{accessToken, user}` plus the httpOnly refresh cookie; an existing-user
 *    join posts neither name nor password.
 */

import { apiClient } from '@/services'
import { setAccessToken } from '@/features/auth/services/authService.http'

/** The validated invitation's guest-facing facts. */
export interface ApiInviteValidation {
  email?: string
  /** The inviting organisation's slug — the landing's active org. */
  orgId?: string
  orgName?: string
  role?: string
  inviterName?: string
  isExistingUser?: boolean
  /**
   * Present on a project invite: where the acceptance lands, and at which access.
   * The server sends all three as JSON `null` on an org-only invite;
   * {@link invitesHttp.validate} drops those, and the `null` stays in the type so
   * a reader cannot assume a present key means a project.
   */
  projectId?: string | null
  projectName?: string | null
  projectAccessLevel?: 'READ' | 'WRITE' | null
}

/** What the accept endpoint answers with (the login shape). */
export interface InviteAcceptResponse {
  accessToken?: string
  user?: { id?: string; name?: string | null; email?: string }
}

export const invitesHttp = {
  /**
   * Validate the token. `AuthController.validateInviteToken` writes the project
   * fields as JSON `null` on an org-only invite; they are dropped here, at the one
   * seam, so "absent" means "no project" for every reader (TASK-7F3EE34E fix wave
   * 1, H1: a `!== undefined` check let a null through and landed on `/project/null`).
   */
  async validate(token: string): Promise<ApiInviteValidation> {
    const row = await apiClient.post<ApiInviteValidation>('/auth/invite/validate', { token })
    return Object.fromEntries(Object.entries(row).filter(([, value]) => value !== null)) as ApiInviteValidation
  },

  accept(input: { token: string; name?: string; password?: string }): Promise<InviteAcceptResponse> {
    return apiClient.post<InviteAcceptResponse>('/auth/invite/accept', input)
  },

  /**
   * Accept and enter: stores the access token in the auth service's module
   * memory (the refresh cookie is already httpOnly-set by the response).
   *
   * @returns true when a session was established.
   */
  async acceptAndEnter(input: { token: string; name?: string; password?: string }): Promise<boolean> {
    return enter(await this.accept(input))
  },

  /**
   * Accept as the SIGNED-IN user (TASK-714FBFBD fix cycle 1b, INV-S9): an
   * existing account with a password accepts here, never on the public route.
   * The server answers fresh tokens that carry the organisation just joined.
   */
  acceptSignedIn(token: string): Promise<InviteAcceptResponse> {
    return apiClient.post<InviteAcceptResponse>('/auth/invite/accept-signed-in', { token })
  },

  async acceptSignedInAndEnter(token: string): Promise<boolean> {
    return enter(await this.acceptSignedIn(token))
  }
}

/** Keep the returned access token in memory; false when the answer carried none. */
function enter(result: InviteAcceptResponse): boolean {
  if (typeof result.accessToken !== 'string' || result.accessToken === '') {
    return false
  }
  setAccessToken(result.accessToken)
  return true
}
